'use client'
import { LazyMotion, domAnimation, AnimatePresence, m } from 'framer-motion'
import * as Dialog from 'primitives/dialog'
import { IconButton } from 'primitives/buttons/icon-button'
import { XCloseIcon } from 'primitives/icons'
import { mergeFormData } from 'utils/dom'
import type { UserAuthResponse } from '@repo/auth-utils'
import { tw } from 'utils/classnames'
import {
  trackLoginModalOpen,
  trackRegistrationModalOpen,
  AuthModalLocations,
  setAmplitudeUserProperties,
} from '@repo/analytics/amplitude/utils'
import { useUsersUpdateFlagsMutation } from 'codegen/generated/user'
import { getUserIdSelector } from 'auth/selectors'
import { useRefetchAuth } from 'auth/use-refetch-auth'
import { Z_INDEX_MODALS } from 'constants/modals'

import { useUserAuthModal, REGISTRATION_LOGIN_SUCCESS_EVENT_NAME } from './user-auth-dialog-context'
import { LoginForm } from './login-form'
import { LoginOtp } from './login-otp'
import { RegisterForm } from './register-form'
import { RegisterOtp } from './register-otp'
import { LostPassword } from './lost-password'
import { trackRegistration } from './utils/track-registration'

export type Step = 'login' | 'login-otp' | 'register' | 'register-otp' | 'forget-password'

export interface UserAuthDialogProps {
  bodyClassName?: string
  children?: React.ReactNode
}

export function UserAuthDialog({ bodyClassName = '', children }: UserAuthDialogProps) {
  const {
    isOpen,
    setIsOpen,
    step,
    setStep,
    formState,
    setFormState,
    errorMessage,
    successMessage,
    setErrorMessage,
    setSuccessMessage,
    successCallback,
    modalOpenChangeCallback,
    trackingOptions,
    skipReloadPageOnSuccess,
  } = useUserAuthModal()
  // eslint-disable-next-line cf-web/codegen-mutation-patterns
  const updateUserFlags = useUsersUpdateFlagsMutation()
  const { refetchAuth } = useRefetchAuth()

  async function onSuccess(me: UserAuthResponse) {
    if (me.user?.flags?.identifyUser) {
      setAmplitudeUserProperties()
      await updateUserFlags.mutateAsync({
        input: {
          identifyUser: false,
        },
      })
    }

    setIsOpen(false)
    //Refetch auth to update user data
    await refetchAuth()
    document.dispatchEvent(new CustomEvent(REGISTRATION_LOGIN_SUCCESS_EVENT_NAME))
    if (successCallback && successCallback.current) {
      successCallback.current(me)
    } else {
      // oxlint-disable-next-line no-lonely-if
      if (!skipReloadPageOnSuccess) {
        window.location.reload()
      }
    }
  }

  async function onRegisterSuccess(me: UserAuthResponse) {
    const userFlags = me.user?.flags

    const userId = getUserIdSelector({ me }) || ''
    if (trackingOptions?.registrationConversion) {
      trackRegistration({
        isGAdsSignup: userFlags?.isGAdsSignup || true,
        isNew: userFlags?.isNew || false,
        userId,
      })
    }

    if (userFlags?.isGAdsSignup || userFlags?.isNew) {
      await updateUserFlags.mutateAsync({
        input: {
          isGAdsSignup: false,
          isNew: false,
        },
      })
    }

    onSuccess(me)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    setErrorMessage(null)
    setSuccessMessage(null)
    if (modalOpenChangeCallback && modalOpenChangeCallback.current) {
      modalOpenChangeCallback.current(open)
    }
  }

  const zIndex = Z_INDEX_MODALS.authentication.react

  return (
    <div className="relative">
      <LazyMotion features={domAnimation}>
        <Dialog.Root defaultOpen={false} open={isOpen} onOpenChange={handleOpenChange}>
          <Dialog.Portal>
            <Dialog.Body
              overlayZIndex={zIndex - 1}
              zIndex={zIndex}
              dismissable={false}
              onInteractOutside={e => e.preventDefault()}
              data-lumo-interactive
            >
              <div
                className={tw(
                  'relative flex h-fit max-h-[100vh] w-full flex-col items-center overflow-auto p-5',
                  bodyClassName,
                )}
              >
                <div className="relative w-full max-w-[400px] grow rounded-[8px] bg-[#ffffff]">
                  {children}
                  <div
                    className={tw(
                      'p-5 pb-10',
                      children ? 'pt-16 md:pt-14 lg:pt-14 xl:pt-14' : 'pt-5',
                    )}
                  >
                    <AnimatePresence initial={false}>
                      {errorMessage !== null || successMessage !== null ? (
                        <m.div
                          exit={{
                            height: 0,
                            opacity: 0,
                          }}
                          initial={{
                            height: 0,
                            opacity: 0,
                          }}
                          animate={{
                            height: 'auto',
                            opacity: 1,
                          }}
                          className="relative left-[-20px] top-[-20px] w-[calc(100%+40px)] overflow-hidden rounded-t-[8px]"
                        >
                          <p
                            className={tw(
                              errorMessage ? 'bg-[#ff4554]' : 'bg-secondary-500',
                              'text-14 p-2 text-center text-[#ffffff]',
                            )}
                          >
                            {errorMessage ? errorMessage : successMessage}
                          </p>
                        </m.div>
                      ) : null}
                    </AnimatePresence>

                    {step === 'login' ? (
                      <div className="flex h-fit w-full flex-col items-center justify-center pt-2">
                        <LoginForm
                          onRegister={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('register')
                            trackRegistrationModalOpen(AuthModalLocations.LOGIN_MODAL)
                          }}
                          onOtp={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('login-otp')
                          }}
                          onLostPassword={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('forget-password')
                          }}
                          onSuccess={onSuccess}
                        />
                      </div>
                    ) : null}
                    {step === 'login-otp' ? (
                      <div className="flex h-fit w-full flex-col items-center justify-center pt-2">
                        <LoginOtp
                          onRegister={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('register')
                            trackRegistrationModalOpen(AuthModalLocations.LOGIN_MODAL)
                          }}
                          onLostPassword={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('forget-password')
                          }}
                          onSuccess={onSuccess}
                        />
                      </div>
                    ) : null}
                    {step === 'register' ? (
                      <div className="flex h-fit w-full flex-col items-center justify-center pt-2">
                        <RegisterForm
                          onLogin={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('login')
                            trackLoginModalOpen(AuthModalLocations.REGISTRATION_MODAL)
                          }}
                          onLoginOtp={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('login-otp')
                          }}
                          onRegisterOtp={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('register-otp')
                          }}
                          onLostPassword={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('forget-password')
                          }}
                          onLoginSuccess={onSuccess}
                          onRegisterSuccess={onRegisterSuccess}
                        />
                      </div>
                    ) : null}
                    {step === 'register-otp' ? (
                      <div className="flex h-fit w-full flex-col items-center justify-center pt-2">
                        <RegisterOtp onSuccess={onRegisterSuccess} />
                      </div>
                    ) : null}
                    {step === 'forget-password' ? (
                      <div className="flex h-fit w-full flex-col items-center justify-center pt-2">
                        <LostPassword
                          onLogin={nextFormState => {
                            setFormState(mergeFormData(formState, nextFormState))
                            setStep('login')
                          }}
                        />
                      </div>
                    ) : null}

                    <div className="absolute right-[-20px] top-[-20px]">
                      <Dialog.Close asChild>
                        <IconButton
                          aria-label="Close dialog"
                          variant="violet"
                          className="hover:bg-aubergine-500 rounded-full bg-[#493675] p-[5px] transition-transform"
                        >
                          <XCloseIcon className="h-[26px] w-[26px] text-[#ffffff]" />
                        </IconButton>
                      </Dialog.Close>
                    </div>
                  </div>
                </div>
              </div>
            </Dialog.Body>
          </Dialog.Portal>
        </Dialog.Root>
      </LazyMotion>
    </div>
  )
}
